import { useDarkMode } from '../context/DarkModeContext'
import { useBrand } from './useBrand'
import type { SurfaceTokens } from '../data/brandThemes'

/**
 * useDarkClasses
 *
 * Returns named Tailwind class strings for page surfaces, text, borders and
 * inputs. Brands with fixed surfaces (AfterTide is always dark navy) return
 * those from the brand theme; LitLaunch derives them from the dark-mode toggle.
 */
export const useDarkClasses = (): SurfaceTokens => {
  const { darkMode } = useDarkMode()
  const { theme } = useBrand()

  if (theme.fixedSurfaces) return theme.fixedSurfaces

  const d = darkMode

  return {
    // ─── Page / Screen ───────────────────────────────────────────────────────
    /** Full-page background */
    page: d ? 'bg-gray-900' : 'bg-white',

    // ─── Surfaces ────────────────────────────────────────────────────────────
    /** Standard card / panel surface */
    card: d ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200',
    /** Card with lighter border (used in some Trove sections) */
    cardSubtle: d
      ? 'bg-gray-800 border-gray-700'
      : 'bg-gray-50 border-gray-100',
    /** Slightly elevated surface (nested cards) */
    cardElevated: d
      ? 'bg-gray-700 border-gray-600'
      : 'bg-gray-100 border-gray-200',
    /** Muted / secondary surface */
    surface: d ? 'bg-gray-900' : 'bg-white',
    /** Very subtle tinted surface */
    surfaceMuted: d ? 'bg-gray-700/50' : 'bg-gray-50',

    // ─── Borders ─────────────────────────────────────────────────────────────
    border: d ? 'border-gray-700' : 'border-gray-200',
    borderSubtle: d ? 'border-gray-700' : 'border-gray-100',
    borderAccent: d ? 'border-gray-600' : 'border-gray-300',
    borderAccentStrong: d ? 'border-gray-500' : 'border-gray-400',
    divider: d ? 'border-gray-700' : 'border-gray-200',

    // ─── Text ────────────────────────────────────────────────────────────────
    textPrimary: d ? 'text-white' : 'text-gray-900',
    textSecondary: d ? 'text-gray-300' : 'text-gray-600',
    textMuted: d ? 'text-gray-400' : 'text-gray-500',
    /** gray-500 fell below readable contrast on gray-800 cards in dark mode;
     * gray-400 keeps it clearly subordinate but legible. */
    textFaint: d ? 'text-gray-400' : 'text-gray-400',
    textHeading: d ? 'text-gray-100' : 'text-gray-900',

    // ─── Form Inputs ─────────────────────────────────────────────────────────
    // Dark placeholders at gray-400 and borders at gray-600: at gray-500/700
    // both nearly vanished against the gray-900 field.
    input: d
      ? 'bg-gray-900 border-gray-600 text-gray-100 placeholder-gray-400'
      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400',
    inputOnCard: d
      ? 'bg-gray-900 border-gray-600 text-gray-100 placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',

    // ─── Navigation ──────────────────────────────────────────────────────────
    nav: d ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
    dropdown: d ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
    dropdownItemHover: d ? 'hover:bg-gray-700' : 'hover:bg-gray-50',

    // ─── Badges / Controls ───────────────────────────────────────────────────
    badgeNeutral: d ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600',
    carouselArrow: d
      ? 'bg-gray-800/90 text-gray-200 border-gray-700'
      : 'bg-white/90 text-gray-700 border-gray-200',
    navTranslucent: d ? 'bg-gray-800/90' : 'bg-white/90',
    chromeIcon: d ? 'text-gray-300' : 'text-gray-700',

    // ─── Utility ─────────────────────────────────────────────────────────────
    /** Raw boolean for cases where a ternary is unavoidable */
    isDark: d,
  }
}

export default useDarkClasses
